/**
 * LLM アグリゲータ（OpenRouter 等）への接続プリセット。
 *
 * ## なぜ「プリセット」という別概念にしたのか
 *
 * aurora-mail の `ProviderEnum` は
 * `none | anthropic | openai | openai_compat | ollama` の 5 値で固定で、
 * `c:\aurora-mail` は**参照専用**（CLAUDE.md）。ここに `openrouter` を足すことは
 * できない。だから mail-sv 側では **プリセットという別の列**で OpenRouter 等を
 * 表し、向こうへ同期するときに **`provider='openai_compat'` + `endpoint`** へ
 * 畳む（`toAuroraLlmPayload()`）。OpenRouter / Together / Groq / Fireworks /
 * DeepInfra / LiteLLM / vLLM / Azure OpenAI はどれも OpenAI 互換の
 * `/chat/completions` を出しているので、向こうの実装を 1 行も変えずに実際に動く。
 *
 * **プリセットを `AI_PROVIDER` の enum に混ぜないこと。** 混ぜた瞬間に
 * 向こうへ送れなくなり、「設定はできるのに同期だけが落ちる」という
 * いちばん気付きにくい壊れ方になる。
 *
 * ## なぜ DB ではなくコードに持つのか
 *
 * プリセットは baseUrl を人に入力させないための表で、**運営が壊れた baseUrl を
 * 1 つ入れると全契約者の AI が同時に止まる**。DB に持つとその経路ができる。
 * 運営が独自のアグリゲータを足したいときは、既存の `openai_compat`（手入力）が
 * そのまま残してあるのでそちらを使う。
 *
 * ## レジデンシーとの関係（**この判断がいちばん重要**）
 *
 * アグリゲータは**実際の推論先を隠す**。OpenRouter に投げた本文がどの国の
 * どの事業者の GPU で処理されるかは、こちらからは決められない。
 * したがって `residencyUnknown: true` のプリセットは
 * **`require_residency` が `any` 以外の組織では選べない**
 * （API で 422・DB の CHECK でも拒否）。ここを緩めると、
 * 「国内で処理する」と契約している相手に処理先の分からない経路を使わせることに
 * なり、レジデンシー要件そのものが意味を失う。
 *
 * 逆に自ホスト型（LiteLLM / vLLM / Azure OpenAI）は**契約者自身が配備先を
 * 知っている**ので `residencyUnknown: false`。ここを一律に true にすると、
 * 「社内 LLM しか使えない」顧客が上位プランの価値を失う。
 */
import { z } from 'zod';

/* ==================================================== 語彙 */

/**
 * プリセットの識別子。**契約者が選ぶ値**で、`org_ai_policies.preset` に入る。
 * 空文字は「プリセットを使わない」（＝従来どおりの手入力 or 運営の共有設定）。
 */
export const AI_PRESET = [
  'openrouter',
  'together',
  'groq',
  'fireworks',
  'deepinfra',
  'azure_openai',
  'litellm',
  'vllm',
  'bedrock',
] as const;
export type AiPreset = (typeof AI_PRESET)[number];

/** 「プリセットを使わない」を表す値。**null にしない**（列は NOT NULL DEFAULT ''）。 */
export const AI_PRESET_NONE = '';
export type AiPresetOrNone = AiPreset | typeof AI_PRESET_NONE;

/** zod 契約。空文字を許すのは「プリセットを外す」を表現するため。 */
export const aiPresetSchema = z.union([z.enum(AI_PRESET), z.literal(AI_PRESET_NONE)]);

/**
 * モデル名の形。検証を**緩く**するためだけに持つ。
 *
 * 厳密な検証をしないのは、アグリゲータのモデル一覧が週単位で入れ替わるため。
 * 「知らないモデル名を弾く」実装にすると、向こうに新しいモデルが出るたびに
 * こちらのデプロイ待ちになる（それは我々が選ぶべき運用ではない）。
 */
export type AiModelFormat = 'vendor/model' | 'flat';

export interface AiPresetDefinition {
  key: AiPreset;
  label: string;
  /**
   * 固定の baseUrl。**null = 契約者が自分の配備先を入力する**（自ホスト型）。
   * 固定できるものは固定する（打ち間違いが SSRF 検査で弾かれると、
   * 契約者には「なぜ弾かれたのか」がまったく分からない）。
   */
  baseUrl: string | null;
  /**
   * モデル一覧のパス（baseUrl からの相対）。空文字なら**一覧を引けない**
   * （Azure OpenAI はデプロイ名が利用者ごとに違い、`/models` に相当する
   * 互換の口が無い）。引けないものは手入力にフォールバックする。
   */
  modelsPath: string;
  /** モデル一覧の取得時に付けるヘッダ。**鍵は含まない**（別に付ける）。 */
  extraHeaders: Readonly<Record<string, string>>;
  modelFormat: AiModelFormat;
  /** 鍵の取り方を書いた向こうのページ。契約者面にそのまま出す。 */
  docsUrl: string;
  /** **実際の推論先を隠すか。** true なら `require_residency` が any の組織だけ。 */
  residencyUnknown: boolean;
  /** 鍵が無いと `/chat/completions` が通らないか（一覧の取得可否とは別）。 */
  requiresApiKey: boolean;
  /**
   * **接続先も鍵も運営（プラットフォーム）が持つプリセットか。**
   *
   * true のものは、契約者も運営も**ポリシー行には接続先 URL を書かない**。
   * 実体は `ai_provider_endpoints` の在庫（リージョンごとに 1 行）で、
   * 同期の直前に `resolveAiEndpoint()` が法域・希望リージョン・health から
   * 1 本を選んで畳み込む。
   *
   * こうしてあるのは Amazon Bedrock のためで、理由は 3 つ:
   *   1. ゲートウェイ（OpenAI 互換 → Bedrock 変換）は**私設ネットワークの中**に
   *      あり、その URL は契約者に見せる情報ではない。ポリシー行に書くと
   *      契約者面の応答に載る。
   *   2. リージョンごとに在庫を持つので、**法域要件で選び分けられる**
   *      （これが Bedrock の価値。§9-4）。ポリシー行に 1 本を焼き付けると
   *      在庫を増やしても切り替わらない。
   *   3. 在庫が全滅したら**止まる**（`resolveAiEndpoint()` が null を返す）。
   *      焼き付けた URL が残っていると、無効にした接続先へ本文が出続ける。
   */
  platformManaged: boolean;
  /** 契約者面に出す 1 行説明。 */
  description: string;
}

/**
 * プリセットの定義表。**ここが唯一の正。**
 *
 * `baseUrl` は各サービスの OpenAI 互換ベース（`/chat/completions` の 1 つ上）に
 * 揃えてある。末尾のスラッシュは付けない（`resolvePresetModelsUrl()` が
 * 連結するときに `//` になる）。
 */
const AI_PRESET_TABLE: Readonly<Record<AiPreset, AiPresetDefinition>> = {
  openrouter: {
    key: 'openrouter',
    label: 'OpenRouter',
    baseUrl: 'https://openrouter.ai/api/v1',
    modelsPath: '/models',
    /*
     * OpenRouter は `HTTP-Referer` / `X-Title` を推奨しており、付けないと
     * レート制限が厳しくなる。`HTTP-Referer` は運営のコンソール URL を
     * `presetRequestHeaders()` の引数で足す（ここに定数で埋めない）。
     */
    extraHeaders: { 'X-Title': 'Aurora Host' },
    modelFormat: 'vendor/model',
    docsUrl: 'https://openrouter.ai/keys',
    residencyUnknown: true,
    requiresApiKey: true,
    platformManaged: false,
    description: '1 本の鍵で多数のベンダーのモデルへ振り分けます（処理先の国は選べません）。',
  },
  together: {
    key: 'together',
    label: 'Together AI',
    baseUrl: 'https://api.together.xyz/v1',
    modelsPath: '/models',
    extraHeaders: {},
    modelFormat: 'vendor/model',
    docsUrl: 'https://api.together.ai/settings/api-keys',
    residencyUnknown: true,
    requiresApiKey: true,
    platformManaged: false,
    description: 'オープンウェイトのモデルを多数ホストしています（処理先の国は選べません）。',
  },
  groq: {
    key: 'groq',
    label: 'Groq',
    baseUrl: 'https://api.groq.com/openai/v1',
    modelsPath: '/models',
    extraHeaders: {},
    modelFormat: 'flat',
    docsUrl: 'https://console.groq.com/keys',
    residencyUnknown: true,
    requiresApiKey: true,
    platformManaged: false,
    description: '応答が速い推論サービスです（処理先の国は選べません）。',
  },
  fireworks: {
    key: 'fireworks',
    label: 'Fireworks AI',
    baseUrl: 'https://api.fireworks.ai/inference/v1',
    modelsPath: '/models',
    extraHeaders: {},
    // `accounts/fireworks/models/...` の形。スラッシュを含むが vendor/model ではない。
    modelFormat: 'flat',
    docsUrl: 'https://fireworks.ai/account/api-keys',
    residencyUnknown: true,
    requiresApiKey: true,
    platformManaged: false,
    description: 'オープンウェイトのモデルを多数ホストしています（処理先の国は選べません）。',
  },
  deepinfra: {
    key: 'deepinfra',
    label: 'DeepInfra',
    baseUrl: 'https://api.deepinfra.com/v1/openai',
    modelsPath: '/models',
    extraHeaders: {},
    modelFormat: 'vendor/model',
    docsUrl: 'https://deepinfra.com/dash/api_keys',
    residencyUnknown: true,
    requiresApiKey: true,
    platformManaged: false,
    description: 'オープンウェイトのモデルを従量課金で使えます（処理先の国は選べません）。',
  },
  azure_openai: {
    key: 'azure_openai',
    label: 'Azure OpenAI',
    // リソース名が契約者ごとに違う（`https://<resource>.openai.azure.com/openai/v1`）。
    baseUrl: null,
    /*
     * **一覧を引かない。** Azure は「モデル」ではなく「デプロイ」を指定する形で、
     * `api-version` が要り、返る形も OpenAI 互換ではない。中途半端に引くより
     * 手入力（デプロイ名）にしたほうが、契約者にとって迷いが少ない。
     */
    modelsPath: '',
    extraHeaders: {},
    modelFormat: 'flat',
    docsUrl: 'https://learn.microsoft.com/azure/ai-services/openai/',
    // リージョンを契約者自身が選んで配備しているので、処理先は分かっている。
    residencyUnknown: false,
    requiresApiKey: true,
    platformManaged: false,
    description: '自社の Azure OpenAI リソースへ接続します（デプロイ名をモデルとして指定）。',
  },
  litellm: {
    key: 'litellm',
    label: 'LiteLLM Proxy',
    baseUrl: null,
    modelsPath: '/models',
    extraHeaders: {},
    modelFormat: 'flat',
    docsUrl: 'https://docs.litellm.ai/docs/simple_proxy',
    residencyUnknown: false,
    requiresApiKey: false,
    platformManaged: false,
    description: '自社で運用している LiteLLM プロキシへ接続します。',
  },
  vllm: {
    key: 'vllm',
    label: 'vLLM',
    baseUrl: null,
    modelsPath: '/models',
    extraHeaders: {},
    modelFormat: 'flat',
    docsUrl: 'https://docs.vllm.ai/en/latest/serving/openai_compatible_server.html',
    residencyUnknown: false,
    requiresApiKey: false,
    platformManaged: false,
    description: '自社で運用している vLLM サーバーへ接続します。',
  },
  /*
   * Amazon Bedrock（§9）。**他のプリセットと性質が 2 つ違う。**
   *
   * ① Bedrock の API は OpenAI 互換ではない（独自形式 + SigV4）。aurora-mail は
   *    Bedrock 未対応で、向こうの `ProviderEnum` は 5 値固定・参照専用なので
   *    `bedrock` を足せない。**OpenAI 互換のゲートウェイを挟む**のが唯一の道で、
   *    向こうからは `openai_compat` + ゲートウェイの内部エンドポイントに見える。
   * ② **法域が確定する**（`residencyUnknown: false`）。リージョンが明示的なので
   *    `ap-northeast-1` → `jp` と言い切れる。だから
   *    **`require_residency='jp'` の組織でも選べる**。アグリゲータでは
   *    応えられない「国内で処理する」という要件に応えられる唯一の経路で、
   *    これが上位プランの売りそのものになる。ここを true に倒すと
   *    Bedrock を足した意味が消える。
   */
  bedrock: {
    key: 'bedrock',
    label: 'Amazon Bedrock',
    /*
     * **固定できない。** 実体はゲートウェイの内部エンドポイントで、
     * リージョンごとに違う。在庫（`ai_provider_endpoints`）に運営が登録し、
     * 同期の直前に解決する（`platformManaged` のコメント参照）。
     */
    baseUrl: null,
    /*
     * **ゲートウェイの `/v1/models` に頼らない。** 一覧は AWS SDK の
     * `ListFoundationModels` で引く（`apps/api/src/services/ai-bedrock.ts`）。
     * 理由は ① ゲートウェイの実装差でモデル名が欠けることがある
     * ② **そのリージョンで実際に有効化されているモデルだけ**を出したい
     * （Bedrock はモデルごとにアカウント単位の有効化が要る。一覧に出たのに
     * 使えないモデルを選ばせると「選べたのに動かない」になる）。
     */
    modelsPath: '',
    extraHeaders: {},
    // `anthropic.claude-sonnet-4-20250514-v1:0` の形。`/` は含まない。
    modelFormat: 'flat',
    docsUrl: 'https://docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html',
    residencyUnknown: false,
    // ゲートウェイとの共有シークレット。**運営が持つ**（契約者は入力しない）。
    requiresApiKey: true,
    platformManaged: true,
    description:
      'このサービスの提供者が用意した Amazon Bedrock へ接続します（処理するリージョン＝法域が確定します）。',
  },
};

/** 表示順（画面のプルダウンの並び）。**アグリゲータを先に出す**。 */
export const AI_PRESETS: readonly AiPresetDefinition[] = AI_PRESET.map((k) => AI_PRESET_TABLE[k]);

/**
 * プリセットを引く。**知らない値では throw しない**（DB に古い値が残っていても
 * 画面が 500 にならないように）。null は「プリセット無し」と同じに扱う。
 */
export function getAiPreset(key: string | null | undefined): AiPresetDefinition | null {
  if (!key) return null;
  return (AI_PRESET_TABLE as Record<string, AiPresetDefinition | undefined>)[key] ?? null;
}

/**
 * 実際の推論先を隠すプリセット（＝レジデンシーを保証できないもの）。
 * **0060 の CHECK 制約と同じ並び**。片方だけ変えないこと。
 */
export const AI_PRESET_RESIDENCY_UNKNOWN: readonly AiPreset[] = AI_PRESET.filter(
  (k) => AI_PRESET_TABLE[k].residencyUnknown,
);

/**
 * **接続先も鍵も運営が持つプリセット**（現時点では Bedrock だけ）。
 * `0070` の CHECK（`org_ai_policies_preset_endpoint_chk`）と同じ並び。
 * **片方だけ変えないこと**（変えると「保存できるのに CHECK で 500」になる）。
 */
export const AI_PRESET_PLATFORM_MANAGED: readonly AiPreset[] = AI_PRESET.filter(
  (k) => AI_PRESET_TABLE[k].platformManaged,
);

/**
 * 接続先を運営が持つプリセットか。**知らない値では false**（DB に古い値が
 * 残っていても、契約者の入力を無視する側へは倒さない）。
 */
export function isPlatformManagedPreset(key: string | null | undefined): boolean {
  const preset = getAiPreset(key);
  return preset !== null && preset.platformManaged;
}

/** プリセットは全部 OpenAI 互換なので、aurora-mail へは必ずこの provider で送る。 */
export const AI_PRESET_PROVIDER = 'openai_compat' as const;

/* ==================================================== Bedrock のリージョン */

/**
 * Bedrock を置ける AWS リージョンと、こちらの語彙との対応（§9-4）。
 *
 * **`aiRegion` と `dataResidency` を対にして持つのが要点。**
 * `region`（距離の話）と `data_residency`（法令の話）は本来別の軸で、
 * 一般の接続先では運営が別々に申告する（ソウルは日本に近いが法域は日本ではない）。
 * ところが Bedrock は**リージョンがそのまま法域を決める**ので、
 * 対応表をコードに持ち、**運営に選ばせない**。ここを人が入力できる形にすると、
 * `us-east-1` の在庫に `jp` と申告できてしまい、
 * **レジデンシー要件を「実際には満たしていないのに満たしている」ことにできる**。
 * 同じ対応表を 0070 の CHECK にも入れてある（片方だけ変えないこと）。
 *
 * ソウル（`ap-northeast-2`）を載せていないのは、法域が `kr` になり
 * `AI_DATA_RESIDENCY`（jp / us / eu）で表せないため。**表せないものを
 * 近い値に丸めない**（丸めた瞬間に上の事故が起きる）。
 */
export interface BedrockRegionDefinition {
  /** AWS のリージョン名。`ListFoundationModels` に渡す値。 */
  awsRegion: string;
  /** 画面に出す名前。 */
  label: string;
  /** こちらの `AI_REGION` の値（距離表と対応が取れている必要がある）。 */
  aiRegion: string;
  /** こちらの `AI_DATA_RESIDENCY` の値。 */
  dataResidency: 'jp' | 'us' | 'eu';
}

export const BEDROCK_REGIONS: readonly BedrockRegionDefinition[] = [
  { awsRegion: 'ap-northeast-1', label: '東京（ap-northeast-1）', aiRegion: 'jp-east', dataResidency: 'jp' },
  { awsRegion: 'ap-northeast-3', label: '大阪（ap-northeast-3）', aiRegion: 'jp-west', dataResidency: 'jp' },
  { awsRegion: 'us-east-1', label: 'バージニア北部（us-east-1）', aiRegion: 'us-east', dataResidency: 'us' },
  { awsRegion: 'us-west-2', label: 'オレゴン（us-west-2）', aiRegion: 'us-west', dataResidency: 'us' },
  { awsRegion: 'eu-west-1', label: 'アイルランド（eu-west-1）', aiRegion: 'eu-west', dataResidency: 'eu' },
  { awsRegion: 'eu-central-1', label: 'フランクフルト（eu-central-1）', aiRegion: 'eu-central', dataResidency: 'eu' },
];

/**
 * 既定のリージョン。**東京**にしてあるのは、Bedrock を足した動機が
 * 「国内で処理する」に応えることだから（一覧の初期表示が us になっていると、
 * 運営が何も考えずに国外の在庫を作れてしまう）。
 */
export const BEDROCK_DEFAULT_REGION = 'ap-northeast-1';

/** AWS リージョン名から引く。知らない名前は null（throw しない）。 */
export function getBedrockRegion(awsRegion: string | null | undefined): BedrockRegionDefinition | null {
  if (!awsRegion) return null;
  return BEDROCK_REGIONS.find((r) => r.awsRegion === awsRegion) ?? null;
}

/** こちらの `AI_REGION` の値から引く（在庫の行から一覧を引くときに使う）。 */
export function bedrockRegionForAiRegion(aiRegion: string | null | undefined): BedrockRegionDefinition | null {
  if (!aiRegion) return null;
  return BEDROCK_REGIONS.find((r) => r.aiRegion === aiRegion) ?? null;
}

/**
 * Bedrock の在庫のリージョンと法域の組み合わせが対応表どおりか。
 * 合っていれば null、違えば**運営にそのまま出す日本語の理由**を返す。
 */
export function checkBedrockRegionResidency(
  aiRegion: string,
  dataResidency: string | null,
): string | null {
  const def = bedrockRegionForAiRegion(aiRegion);
  if (!def) {
    return `Amazon Bedrock を配置できないリージョンです（${BEDROCK_REGIONS.map((r) => r.aiRegion).join(' / ')} から選んでください）`;
  }
  if (dataResidency !== def.dataResidency) {
    return `${def.label} の法域は「${def.dataResidency}」です（リージョンから決まるため変更できません）`;
  }
  return null;
}

/* ---------------------------------------- 運営が入力する内部エンドポイント */

/**
 * **私設ネットワーク内のホスト名を許すかどうかは、ここだけで決める。**
 *
 * Bedrock のゲートウェイは私設サブネット（インターネットへ出さない）に置くので、
 * その URL は `assertOutboundUrlAllowed()`（SSRF ガード）を**通らない**。
 * ガードを緩めるのではなく、**そもそも CP からその URL へ HTTP を出さない**
 * 形にしてある:
 *
 *   - 推論の本文を送るのは **aurora-mail**（同じ VPC の中）であって CP ではない。
 *   - CP は在庫の疎通確認（`ai.endpoint.healthcheck`）でだけ URL を叩くが、
 *     **運営が持つプリセットの在庫は疎通の対象から外してある**
 *     （`apps/worker/src/workers/ai-usage.ts`）。
 *
 * したがって、この関数が見るのは「CP が叩かないとしても、そもそも書かせては
 * いけない形」だけ:
 *   - http / https 以外のスキーム
 *   - URL に埋め込んだ資格情報（そのまま DB とログに残る）
 *   - **クラウドのメタデータサービス**（169.254.0.0/16 と既知の名前）。
 *     私設アドレスは許すが、ここだけは許さない。許すと、将来この URL を
 *     叩く経路が 1 本増えた瞬間に、インスタンスの資格情報が漏れる形になる。
 *
 * 通れば null、通らなければ**運営にそのまま出す日本語の理由**を返す。
 */
export function checkPlatformManagedEndpointUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return '接続先の URL の形式が不正です';
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    return '接続先の URL は http または https で指定してください';
  }
  if (url.username !== '' || url.password !== '') {
    return 'URL に利用者名やパスワードを埋め込まないでください';
  }
  const host = url.hostname.toLowerCase().replace(/^\[|\]$/g, '');
  if (BEDROCK_BLOCKED_HOSTS.has(host) || host.startsWith('169.254.') || host.startsWith('fd00:ec2:')) {
    return 'クラウドのメタデータサービスは接続先に指定できません';
  }
  return null;
}

/** メタデータサービスの既知の名前。**IP だけを塞いでも名前で回り込める。** */
const BEDROCK_BLOCKED_HOSTS = new Set<string>([
  'metadata.google.internal',
  'metadata.goog',
  'metadata',
  '0.0.0.0',
]);

/* ==================================================== 純関数 */

/**
 * プリセットの実効 baseUrl。
 *
 * - 固定型（OpenRouter 等）… **契約者の入力を無視して定義の値を使う。**
 *   ここで入力を優先すると「プリセットを選んだのに別のホストへ本文が出る」
 *   経路ができ、プリセットの意味（打ち間違いを構造で潰す）が消える。
 * - 自ホスト型（LiteLLM / vLLM / Azure OpenAI）… 契約者の入力が必須。
 *   空なら null を返す（呼び出し側が 400 にする）。
 *
 * 末尾のスラッシュは落とす（連結時の `//` を防ぐ）。
 */
export function resolvePresetEndpoint(
  preset: AiPresetDefinition,
  customBaseUrl: string,
): string | null {
  if (preset.baseUrl !== null) return preset.baseUrl;
  const trimmed = customBaseUrl.trim().replace(/\/+$/, '');
  return trimmed === '' ? null : trimmed;
}

/** モデル一覧の URL。引けないプリセット（`modelsPath` が空）は null。 */
export function resolvePresetModelsUrl(
  preset: AiPresetDefinition,
  baseUrl: string,
): string | null {
  if (preset.modelsPath === '') return null;
  const base = baseUrl.replace(/\/+$/, '');
  if (base === '') return null;
  return `${base}/${preset.modelsPath.replace(/^\/+/, '')}`;
}

/**
 * このプリセットを、そのレジデンシー要件の組織が選べるか。
 *
 * **`any` 以外の組織はアグリゲータを選べない。** アグリゲータは実際の推論先を
 * 隠すので、`jp` を要求している組織に使わせると要件を満たしていることを
 * 誰も保証できない。緩めるならレジデンシー機能そのものを畳むべきで、
 * ここだけ例外にしてはいけない。
 */
export function isPresetAllowedForResidency(
  preset: AiPresetDefinition,
  requireResidency: string,
): boolean {
  if (!preset.residencyUnknown) return true;
  return requireResidency === 'any';
}

/** 画面と API エラーに出す理由（**なぜ選べないかを必ず見せる**）。 */
export const AI_PRESET_RESIDENCY_REJECT_JA =
  'このサービスは実際の処理先（国・事業者）を公開しないため、データレジデンシー要件を設定している組織では選べません。要件を「指定なし」に戻すか、リージョンが確定している接続先をお使いください。';

export interface AiModelCheck {
  ok: boolean;
  /** 落ちた理由。ok のときは null。 */
  message: string | null;
}

/**
 * モデル名がプリセットの形に合っているか。**緩く見る。**
 *
 * 見るのは 2 つだけ:
 *   - 空でないこと（空は「運営の既定に従う」の意味で、プリセットには無い）
 *   - `vendor/model` 形のサービスに `/` の無い名前を入れていないこと
 *
 * 一覧に載っているかどうかは**見ない**。一覧は取得できないことがあり
 * （鍵が無い・向こうが落ちている）、そのときに設定できないのは劣化が大きい。
 */
export function validateModelForPreset(preset: AiPresetDefinition, model: string): AiModelCheck {
  const value = model.trim();
  if (value === '') {
    return { ok: false, message: 'このサービスではモデルを指定してください' };
  }
  if (preset.modelFormat === 'vendor/model' && !value.includes('/')) {
    return {
      ok: false,
      message: `${preset.label} のモデル名は「ベンダー名/モデル名」の形で指定してください`,
    };
  }
  return { ok: true, message: null };
}

/**
 * モデル一覧を取りに行くときのヘッダ。
 *
 * **鍵はここでは付けない**（呼び出し側が `Authorization` を組み立てる）。
 * 鍵をこの純関数に通すと、例外メッセージやログに載る経路ができる。
 *
 * `attributionUrl` は OpenRouter の `HTTP-Referer`。運営のコンソール URL を
 * 渡す。空なら付けない（存在しない URL を送るより無いほうがよい）。
 */
export function presetRequestHeaders(
  preset: AiPresetDefinition,
  attributionUrl = '',
): Record<string, string> {
  const headers: Record<string, string> = { accept: 'application/json', ...preset.extraHeaders };
  if (preset.key === 'openrouter' && attributionUrl !== '') {
    headers['HTTP-Referer'] = attributionUrl;
  }
  return headers;
}

/* ==================================================== モデル一覧の解釈 */

export interface AiPresetModel {
  id: string;
  /** 画面に出す名前。無ければ id と同じ。 */
  label: string;
  /** 文脈長（分かれば）。契約者がモデルを選ぶときの唯一の実用的な指標。 */
  contextLength: number | null;
}

/**
 * `/models` の応答からモデル一覧を取り出す。**形の揺れを吸収するのはここだけ。**
 *
 * OpenAI 互換は `{ data: [...] }` だが、Together は素の配列を返すことがある。
 * どちらでも読めるようにしておかないと、「プリセットを足したら一覧だけ空」に
 * なって原因が分からなくなる。
 *
 * **不明な形なら空配列を返す**（throw しない）。一覧が引けないことは
 * 手入力へのフォールバックであって、エラーではない。
 */
export function parseAiPresetModels(body: unknown, limit = 500): AiPresetModel[] {
  const list = Array.isArray(body)
    ? body
    : Array.isArray((body as { data?: unknown } | null)?.data)
      ? ((body as { data: unknown[] }).data)
      : [];

  const out: AiPresetModel[] = [];
  for (const raw of list) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id : typeof row.name === 'string' ? row.name : '';
    if (id === '' || id.length > 200) continue;
    const name = typeof row.name === 'string' && row.name !== '' ? row.name : id;
    const ctx =
      typeof row.context_length === 'number'
        ? row.context_length
        : typeof row.context_window === 'number'
          ? row.context_window
          : null;
    out.push({
      id,
      label: name.length > 200 ? name.slice(0, 200) : name,
      contextLength: ctx !== null && Number.isFinite(ctx) && ctx > 0 ? Math.trunc(ctx) : null,
    });
    if (out.length >= limit) break;
  }
  // 並びは向こう任せにしない（画面が実行のたびに入れ替わると探せない）。
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/**
 * Bedrock の `ListFoundationModels` の応答（`modelSummaries`）を一覧へ写す。
 *
 * **AWS SDK を `packages/shared` に持ち込まないための純関数。**
 * SDK の呼び出しは `apps/api/src/services/ai-bedrock.ts` にあり、
 * 返ってきた配列をそのままここへ渡す。こうしておくと
 * **AWS が無い環境でも判定そのものをテストできる**（この判定が
 * 「一覧に出たのに使えない」を防ぐ唯一の場所なので、必ず固定したい）。
 *
 * 残すのは 2 つの条件を**両方**満たすものだけ:
 *   1. `modelLifecycle.status === 'ACTIVE'` … 廃止予定（`LEGACY`）を出さない。
 *   2. `inferenceTypesSupported` に `ON_DEMAND` を含む … プロビジョンド
 *      スループット専用のモデルは、購入していないアカウントでは呼べない。
 *
 * さらに **テキストを返さないモデル（画像生成・埋め込み）を落とす**。
 * Webメールの AI（要約・返信案・分類）はどれもテキスト生成なので、
 * 埋め込みモデルを選べる一覧は誤りしか生まない。
 * ただし `outputModalities` が**無い**応答では落とさない
 * （SDK の版で欠けることがあり、欠けたときに全部消えるほうが害が大きい）。
 *
 * **形が読めなければ空配列を返す**（throw しない）。一覧が引けないことは
 * 手入力へのフォールバックであって、エラーではない。
 */
export function parseBedrockFoundationModels(body: unknown, limit = 500): AiPresetModel[] {
  const list = Array.isArray(body)
    ? body
    : Array.isArray((body as { modelSummaries?: unknown } | null)?.modelSummaries)
      ? (body as { modelSummaries: unknown[] }).modelSummaries
      : [];

  const out: AiPresetModel[] = [];
  for (const raw of list) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Record<string, unknown>;

    const id = typeof row.modelId === 'string' ? row.modelId : '';
    if (id === '' || id.length > 200) continue;

    const lifecycle = row.modelLifecycle as { status?: unknown } | undefined;
    if (typeof lifecycle?.status === 'string' && lifecycle.status !== 'ACTIVE') continue;

    const inference = row.inferenceTypesSupported;
    if (!Array.isArray(inference) || !inference.includes('ON_DEMAND')) continue;

    const outputs = row.outputModalities;
    if (Array.isArray(outputs) && !outputs.includes('TEXT')) continue;

    const modelName = typeof row.modelName === 'string' ? row.modelName : '';
    const providerName = typeof row.providerName === 'string' ? row.providerName : '';
    const label = modelName === '' ? id : providerName === '' ? modelName : `${providerName} ${modelName}`;

    out.push({
      id,
      label: label.length > 200 ? label.slice(0, 200) : label,
      // `ListFoundationModels` は文脈長を返さない。**推測で埋めない。**
      contextLength: null,
    });
    if (out.length >= limit) break;
  }
  out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}
